/**
 * The filter fields the coordination reset keys are built from. A minimal
 * structural subset of `BaseCommunityFilters` — `BaseCommunityFilters` is
 * assignable to it — so this stays decoupled from the full filter type.
 */
export type CoordinationFilters = {
  status?: string
  sortOption?: string
  publishedAt?: string
  postId?: string
}

export type CoordinationResetKeys = {
  resetExpansionKey: string
  resetResolutionKey: string
}

/**
 * Builds the two reset-trigger strings the coordination driver diffs to know
 * when to collapse expansion vs. clear the session resolution set.
 *
 * - **Expansion** resets on any filter that reorders or refilters the list —
 *   status, sort, date range, post — plus the channel selection.
 * - **Resolution** resets on a deliberately narrower set: status, post, and
 *   channel only, NOT sort/date. So reordering the list (a sort change) keeps a
 *   card the user resolved this session greyed-out instead of snapping it back.
 *
 * Channels are sorted for the expansion key (it identifies the selection set)
 * but kept in list order for the resolution key — matching exactly what both
 * lists computed inline before this was shared. This subtle split is the one
 * piece where duplication was a real correctness risk, so it lives here once.
 */
export const buildCoordinationResetKeys = (
  filters: CoordinationFilters | undefined,
  channelIds: readonly string[],
): CoordinationResetKeys => ({
  resetExpansionKey: [
    filters?.status,
    filters?.sortOption,
    filters?.publishedAt,
    filters?.postId,
    [...channelIds].toSorted().join(','),
  ].join('|'),
  resetResolutionKey: [
    channelIds.join(','),
    filters?.status,
    filters?.postId,
  ].join('|'),
})
